import React, { useState } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { EducationEntry } from '../../types/wizard';

const Step3Practices: React.FC = () => {
  const { wizardData, updateStepData, saveWizardData, nextStep, previousStep, isLoading, errors } =
    useWizard();

  const [formData, setFormData] = useState({
    years_of_experience: wizardData.years_of_experience || 0,
    education: wizardData.education || [],
    certifications: wizardData.certifications || [],
    professional_affiliations: wizardData.professional_affiliations || [],
    languages_spoken: wizardData.languages_spoken || [],
  });

  const [educationForm, setEducationForm] = useState<EducationEntry>({
    degree: '',
    institution: '',
    year: new Date().getFullYear(),
  });

  const [certificationInput, setCertificationInput] = useState('');
  const [affiliationInput, setAffiliationInput] = useState('');
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const commonAffiliations = [
    'American Academy of Dermatology',
    'American Society of Plastic Surgeons',
    'American Med Spa Association',
    'International Association for Physicians in Aesthetic Medicine',
    'American Society for Aesthetic Plastic Surgery',
    'American Board of Medical Specialties',
  ];

  const addEducation = () => {
    if (educationForm.degree && educationForm.institution && educationForm.year) {
      setFormData((prev) => ({
        ...prev,
        education: [...prev.education, educationForm],
      }));
      setEducationForm({
        degree: '',
        institution: '',
        year: new Date().getFullYear(),
      });
    }
  };

  const removeEducation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const addCertification = (certification: string) => {
    if (certification && !formData.certifications.includes(certification)) {
      setFormData((prev) => ({
        ...prev,
        certifications: [...prev.certifications, certification],
      }));
    }
    setCertificationInput('');
  };

  const removeCertification = (certification: string) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((c) => c !== certification),
    }));
  };

  const addAffiliation = (affiliation: string) => {
    if (affiliation && !formData.professional_affiliations.includes(affiliation)) {
      setFormData((prev) => ({
        ...prev,
        professional_affiliations: [...prev.professional_affiliations, affiliation],
      }));
    }
    setAffiliationInput('');
  };

  const removeAffiliation = (affiliation: string) => {
    setFormData((prev) => ({
      ...prev,
      professional_affiliations: prev.professional_affiliations.filter((a) => a !== affiliation),
    }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (formData.years_of_experience < 0) {
      errors.experience = 'Years of experience cannot be negative';
    }

    if (formData.education.length === 0) {
      errors.education = 'At least one education entry is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (validateForm()) {
      // Update local state with step 3 data
      await updateStepData(formData);

      // Save all wizard data to backend (pass step 3 data explicitly; context state
      // captured by saveWizardData does not include it yet)
      const newSessionId = await saveWizardData(formData);

      if (newSessionId) {
        // Step 4 explains when the CLOUT score is calculated (no score before signup)
        nextStep();
      }
      // On failure, errors.general / errors.email from context are rendered below
    }
  };

  // Keep unsaved edits when navigating back
  const handleBack = async () => {
    await updateStepData(formData);
    previousStep();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="font-['Ovo',_serif] text-[24px] sm:text-[28px] lg:text-[32px] text-[#1f1f1f] mb-6 sm:mb-8">
        Professional Background
      </h2>

      <div className="space-y-4 sm:space-y-6">
        {/* Years of Experience */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-1">
            Years of Experience *
          </label>
          <input
            type="number"
            value={formData.years_of_experience}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                years_of_experience: parseInt(e.target.value) || 0,
              }))
            }
            min="0"
            className={`h-12 sm:h-14 w-full border ${
              localErrors.experience ? 'border-red-500' : 'border-[#eaeaea]'
            } px-4 sm:px-6 py-3 sm:py-4 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f]`}
          />
          {localErrors.experience && (
            <span className="text-red-500 text-[14px] mt-1">{localErrors.experience}</span>
          )}
        </div>

        {/* Education */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-2 sm:mb-3">
            Education *
          </label>

          <div className="bg-[#f5f5f5] p-3 sm:p-4 mb-4">
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text"
                value={educationForm.degree}
                onChange={(e) => setEducationForm((prev) => ({ ...prev, degree: e.target.value }))}
                placeholder="Degree (e.g., MD, RN)"
                className="h-12 border border-[#eaeaea] px-4 py-3 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] placeholder:text-[#767472] bg-white"
              />
              <input
                type="text"
                value={educationForm.institution}
                onChange={(e) =>
                  setEducationForm((prev) => ({ ...prev, institution: e.target.value }))
                }
                placeholder="Institution"
                className="h-12 border border-[#eaeaea] px-4 py-3 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] placeholder:text-[#767472] bg-white"
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  value={educationForm.year}
                  onChange={(e) =>
                    setEducationForm((prev) => ({ ...prev, year: parseInt(e.target.value) || 0 }))
                  }
                  placeholder="Year"
                  min="1900"
                  max={new Date().getFullYear()}
                  className="h-12 flex-1 border border-[#eaeaea] px-4 py-3 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] bg-white"
                />
                <button
                  type="button"
                  onClick={addEducation}
                  className="px-4 h-12 min-h-[44px] bg-[#c6a664] text-white font-['Ovo',_serif] hover:bg-[#b89654] transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Education List */}
          {formData.education.length > 0 && (
            <div className="space-y-2 mb-3">
              {formData.education.map((edu, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white border border-[#eaeaea]"
                >
                  <div className="font-['Ovo',_serif] text-[16px] text-[#1f1f1f]">
                    {edu.degree} - {edu.institution} ({edu.year})
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEducation(index)}
                    className="text-[#767472] hover:text-[#1f1f1f]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {localErrors.education && (
            <span className="text-red-500 text-[14px] mt-1 block">{localErrors.education}</span>
          )}
        </div>

        {/* General Error */}
        {(localErrors.general || errors.general || errors.email) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {typeof (localErrors.general || errors.general || errors.email) === 'object'
              ? JSON.stringify(localErrors.general || errors.general || errors.email)
              : localErrors.general || errors.general || errors.email}
          </div>
        )}

        {/* Certifications */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-2 sm:mb-3">
            Certifications & Licenses
          </label>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              value={certificationInput}
              onChange={(e) => setCertificationInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCertification(certificationInput);
                }
              }}
              placeholder="Add certification (e.g., Board Certified Dermatologist)"
              className="flex-1 h-12 sm:h-14 border border-[#eaeaea] px-4 sm:px-6 py-3 sm:py-4 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
            />
            <button
              type="button"
              onClick={() => addCertification(certificationInput)}
              className="px-6 h-12 sm:h-14 min-h-[44px] bg-[#c6a664] text-white font-['Ovo',_serif] hover:bg-[#b89654] transition-colors"
            >
              Add
            </button>
          </div>

          {formData.certifications.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.certifications.map((cert) => (
                <div
                  key={cert}
                  className="px-4 py-2 bg-[#f5f5f5] text-[14px] font-['Ovo',_serif] flex items-center gap-2"
                >
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeCertification(cert)}
                    className="text-[#767472] hover:text-[#1f1f1f]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Professional Affiliations */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-2 sm:mb-3">
            Professional Affiliations
          </label>

          {/* Common Affiliations */}
          <div className="flex flex-wrap gap-2 mb-3">
            {commonAffiliations.map((affiliation) => (
              <button
                key={affiliation}
                type="button"
                onClick={() => addAffiliation(affiliation)}
                disabled={formData.professional_affiliations.includes(affiliation)}
                className={`px-3 py-2 min-h-[44px] border text-[12px] sm:text-[14px] font-['Ovo',_serif] transition-colors ${
                  formData.professional_affiliations.includes(affiliation)
                    ? 'border-[#c6a664] bg-[#c6a664] text-white cursor-not-allowed'
                    : 'border-[#eaeaea] hover:border-[#c6a664] hover:text-[#c6a664]'
                }`}
              >
                {affiliation}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              value={affiliationInput}
              onChange={(e) => setAffiliationInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAffiliation(affiliationInput);
                }
              }}
              placeholder="Add other affiliation"
              className="flex-1 h-12 sm:h-14 border border-[#eaeaea] px-4 sm:px-6 py-3 sm:py-4 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
            />
            <button
              type="button"
              onClick={() => addAffiliation(affiliationInput)}
              className="px-6 h-12 sm:h-14 min-h-[44px] bg-[#c6a664] text-white font-['Ovo',_serif] hover:bg-[#b89654] transition-colors"
            >
              Add
            </button>
          </div>

          {formData.professional_affiliations.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.professional_affiliations.map((affiliation) => (
                <div
                  key={affiliation}
                  className="px-4 py-2 bg-[#f5f5f5] text-[14px] font-['Ovo',_serif] flex items-center gap-2"
                >
                  {affiliation}
                  <button
                    type="button"
                    onClick={() => removeAffiliation(affiliation)}
                    className="text-[#767472] hover:text-[#1f1f1f]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
        <button
          onClick={handleBack}
          className="order-2 sm:order-1 px-6 sm:px-8 py-3 min-h-[44px] border border-[#1f1f1f] text-[#1f1f1f] font-['Ovo',_serif] text-[16px] sm:text-[18px] uppercase hover:bg-[#f5f5f5] transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="order-1 sm:order-2 px-6 sm:px-8 py-3 min-h-[44px] bg-[#1f1f1f] text-white font-['Ovo',_serif] text-[16px] sm:text-[18px] uppercase hover:bg-[#2f2f2f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default Step3Practices;
